import type {
	PortfolioLabComposition,
	PortfolioLabCompositionInstrument,
	PortfolioLabCompositionPortfolio,
} from "../../ctx";

/**
 * `baseline`, plus every holding of `current` it lacks at weight 0. A rebaseline PUT leaves the
 * holdings it does not name at their current weight, so a holding added after the baseline would
 * otherwise enter the restored baseline as if it had always been there, and its Δ would vanish.
 */
export function baselineCoveringComposition(
	baseline: PortfolioLabComposition[],
	current: PortfolioLabComposition[],
): PortfolioLabComposition[] {
	const coveredBaseline = baseline.map((holding): PortfolioLabComposition => {
		if (holding.type === "instrument") {
			return holding;
		}
		const currentPortfolio = current.find(
			(c): c is PortfolioLabCompositionPortfolio => c.type === "portfolio" && c.uuid === holding.uuid,
		);
		const missingLines = (currentPortfolio?.instruments ?? []).filter(
			(line) => !holding.instruments.some((baselineLine) => baselineLine.tickerName === line.tickerName),
		);
		if (missingLines.length === 0) {
			return holding;
		}
		return { ...holding, instruments: [...holding.instruments, ...missingLines.map(zeroLine)] };
	});
	const missingHoldings = current
		.filter((holding) => !baseline.some((baselineHolding) => sameHolding(baselineHolding, holding)))
		.map(zeroHolding);
	return [...coveredBaseline, ...missingHoldings];
}

function sameHolding(a: PortfolioLabComposition, b: PortfolioLabComposition): boolean {
	if (a.type === "instrument" && b.type === "instrument") {
		return a.tickerName === b.tickerName;
	}
	if (a.type === "portfolio" && b.type === "portfolio") {
		return a.uuid === b.uuid;
	}
	return false;
}

function zeroLine(line: PortfolioLabCompositionPortfolio["instruments"][number]) {
	return { ...line, weight: 0, monetaryWeight: 0 };
}

function zeroHolding(holding: PortfolioLabComposition): PortfolioLabComposition {
	if (holding.type === "instrument") {
		return { ...holding, weight: 0, monetaryWeight: 0 } satisfies PortfolioLabCompositionInstrument;
	}
	return { ...holding, weight: 0, monetaryWeight: 0, instruments: holding.instruments.map(zeroLine) };
}
